import {
  formatValidQueryParamUrlCommaSeparated,
  openFile
} from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  Form,
  FormDocumentInput,
  FormInput,
  FormsResponse,
  PurchaseAgreementFormInput
} from "./types/forms.type";

const GET_FORM_QUERY =
  "{id,form_template{id,title,type},created_at,created_by,document,signed_at,signature,updated_at,is_public}";

const extendedFormsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getForms: builder.query<
      FormsResponse,
      { patientId: number; formTemplate?: number }
    >({
      query: ({ patientId, formTemplate }) =>
        `/forms/?ordering=-id&patient=${patientId}&form_template=${
          formTemplate || ""
        }&query=${GET_FORM_QUERY}`,
      providesTags: ["Form"]
    }),
    getForm: builder.query<Form, number>({
      query: (formId) => `/forms/${formId}/?query=${GET_FORM_QUERY}`,
      providesTags: ["Form"]
    }),
    getFormFileUrl: builder.query<
      string,
      { formId: number; appointment?: number; clinic?: number }
    >({
      query: ({ formId, appointment, clinic }) => {
        const contextParams = formatValidQueryParamUrlCommaSeparated({
          appointment,
          clinic
        });
        return {
          url: `/forms/${formId}/?query=${GET_FORM_QUERY}${contextParams ? `&${contextParams}` : ""}`,
          method: "GET",
          headers: {
            Accept: "application/pdf"
          },
          responseHandler: async (response) => {
            if (response.ok) {
              const blob = await response.blob();
              const fileUrl = window.URL.createObjectURL(blob);
              return fileUrl;
            } else {
              console.error(
                `GetFormFileUrl Error: ${response.status} ${response.statusText}`
              );
              throw new Error(
                `Failed to fetch form file URL (${response.statusText})`
              );
            }
          },
          cache: "no-cache"
        };
      }
    }),
    fetchForms: builder.mutation<FormsResponse, number>({
      query: (patientId) => ({
        url: `/forms/?patient=${patientId}&query=${GET_FORM_QUERY}`,
        cache: "no-cache"
      })
    }),
    generatePurchaseAgreement: builder.mutation<
      any,
      PurchaseAgreementFormInput
    >({
      query({
        saleId,
        formTemplateId,
        patientSignature,
        purchaseAgreementDate,
        trialPeriodDurationDays,
        memo,
        is_public,
        warranty_duration,
        restocking_return_fee,
        loss_damage_deductible
      }: PurchaseAgreementFormInput) {
        const formData = new FormData();
        formData.append("template_id", formTemplateId.toString());
        if (is_public) {
          formData.append("is_public", "true");
        }
        const templateData = {
          generated_at: purchaseAgreementDate || "",
          trial_period_duration_days: trialPeriodDurationDays?.toString() || "",
          memo: memo || "",
          restocking_return_fee: restocking_return_fee?.toString() || "",
          warranty_duration: warranty_duration?.toString() || "",
          loss_damage_deductible: loss_damage_deductible?.toString() || ""
        };
        formData.append("template_data", JSON.stringify(templateData));
        if (patientSignature) {
          formData.append("patient_signature", patientSignature);
        }
        return {
          url: `/sales/${saleId}/purchase-agreements/`,
          method: "POST",
          body: formData,
          responseHandler: async (response) => {
            if (!response.ok) {
              console.error(
                `GeneratePurchaseAgreement Error: ${response.status} ${response.statusText} saleId=${saleId}, formTemplateId=${formTemplateId}`
              );
              throw new Error(response.statusText);
            }
            const blob = await response.blob();
            return blob;
          },
          cache: "no-cache"
        };
      },
      invalidatesTags: ["Form"]
    }),
    createForm: builder.mutation<
      any,
      { formData: Omit<FormInput, "id">; asFile: boolean; shouldOpen?: boolean }
    >({
      query({ formData, asFile = false, shouldOpen = true }) {
        const { order, clinic, appointment, footer_signer_id, ...restData } = formData;
        return {
          url: `/forms/?patient=${
            formData.patient
          }&${formatValidQueryParamUrlCommaSeparated({
            order,
            clinic,
            appointment,
            footer_signer_id
          })}`,
          method: "POST",
          body: restData,
          headers: {
            Accept: asFile ? "application/pdf" : undefined
          },
          responseHandler: async (response) => {
            if (!asFile) {
              return response.json();
            }
            if (!response.ok) {
              console.error(
                `CreateForm Error: ${response.status} ${response.statusText}`
              );
              throw new Error(response.statusText);
            }

            const blob = await response.blob();

            if (shouldOpen) {
              const fileUrl = window.URL.createObjectURL(blob);
              openFile(fileUrl);
            } else {
              return blob;
            }
          },
          cache: "no-cache"
        };
      },
      invalidatesTags: ["Form"]
    }),
    deleteForm: builder.mutation<void, number>({
      query: (formId) => ({
        url: `/forms/${formId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Form"]
    }),
    downloadForm: builder.mutation<any, number>({
      query(formId) {
        return {
          url: `/forms/${formId}/`,
          method: "GET",
          headers: {
            Accept: "application/pdf"
          },
          responseHandler: async (response) => {
            const blob = await response.blob();
            const fileUrl = window.URL.createObjectURL(blob);

            // Get filename from Content-Disposition header or use default
            let filename = "repair_form.pdf";
            const contentDisposition = response.headers.get(
              "Content-Disposition"
            );
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(
                /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
              );
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, "");
              }
            }

            openFile(fileUrl, false, filename);

            // Clean up the blob URL after a delay to allow download to complete
            setTimeout(() => {
              window.URL.revokeObjectURL(fileUrl);
            }, 5000);
          },
          cache: "no-cache"
        };
      },
      invalidatesTags: []
    }),
    updateFormDocument: builder.mutation<
      any,
      FormDocumentInput & { asFile?: boolean }
    >({
      query: ({ id, appointment, clinic, footer_signer_id, asFile, formData }) => ({
        url: `/forms/${id}/${formatValidQueryParamUrlCommaSeparated(
          {
            appointment,
            clinic,
            footer_signer_id
          },
          true
        )}`,
        headers: {
          Accept: asFile ? "application/pdf" : undefined
        },
        method: "PATCH",
        body: formData,
        responseHandler: async (response) => {
          if (!asFile) {
            return response.json();
          }
          if (!response.ok) {
            console.error(
              `UpdateFormDocument Error: ${response.status} ${response.statusText}`
            );
            throw new Error(response.statusText);
          }

          const blob = await response.blob();
          return blob;
        },
        cache: "no-cache"
      }),

      invalidatesTags: ["Users"]
    })
  })
});

export const {
  useGetFormsQuery,
  useGetFormQuery,
  useGeneratePurchaseAgreementMutation,
  useDownloadFormMutation,
  useCreateFormMutation,
  useFetchFormsMutation,
  useGetFormFileUrlQuery,
  useLazyGetFormFileUrlQuery,
  useDeleteFormMutation,
  useUpdateFormDocumentMutation
} = extendedFormsApi;
